import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { billingAPI, UsageSummary, UsageWarning } from '../../services/api';

export const UsageDisplay: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [warnings, setWarnings] = useState<UsageWarning[]>([]);
  const [period, setPeriod] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUsage();
  }, []);

  const loadUsage = async () => {
    try {
      const response = await billingAPI.getCurrentUsage();
      setUsage(response.data.usage);
      setWarnings(response.data.warnings);
      setPeriod(response.data.period);
    } catch (err) {
      setError(t('billing.errors.loadUsage'));
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (percentage: number | null): string => {
    if (percentage === null) return 'bg-cyan-500';
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 90) return 'bg-red-400';
    if (percentage >= 75) return 'bg-amber-500';
    return 'bg-cyan-500';
  };

  const getWarningBanner = (warning: UsageWarning) => {
    const colors = {
      warning: isDark ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-yellow-50 border-yellow-200 text-yellow-800',
      critical: isDark ? 'bg-orange-500/10 border-orange-500/30 text-orange-400' : 'bg-orange-50 border-orange-200 text-orange-800',
      exceeded: isDark ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-red-50 border-red-200 text-red-800',
    };

    return (
      <div
        key={warning.metric}
        className={`border rounded-lg p-3 ${colors[warning.status]}`}
      >
        <p className="text-sm font-medium">
          {t(`billing.warnings.${warning.status}`, {
            metric: t(`billing.metrics.${warning.metric}`),
            percentage: warning.percentage.toFixed(0),
          })}
        </p>
      </div>
    );
  };

  const formatLimit = (limit: number | null): string => {
    if (limit === null) return t('billing.unlimited');
    return limit.toLocaleString();
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className={`h-4 rounded w-1/4 mb-2 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
            <div className={`h-3 rounded w-full ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`px-4 py-3 rounded-lg ${isDark ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'}`}>
        {error}
      </div>
    );
  }

  if (!usage) {
    return null;
  }

  const metrics = [
    {
      key: 'users',
      label: t('billing.metrics.users'),
      ...usage.users,
    },
    {
      key: 'time_entries',
      label: t('billing.metrics.time_entries'),
      ...usage.time_entries,
    },
    {
      key: 'ai_analysis',
      label: t('billing.metrics.ai_analysis'),
      ...usage.ai_analysis,
    },
  ];

  return (
    <div className="space-y-6">
      {warnings.length > 0 && (
        <div className="space-y-2">
          {warnings.map((warning) => getWarningBanner(warning))}
        </div>
      )}

      <div className={`text-sm mb-4 ${isDark ? 'text-slate-300' : 'text-gray-500'}`}>
        {t('billing.usagePeriod')}: <span className={isDark ? 'text-white font-medium' : 'text-gray-700 font-medium'}>{period}</span>
      </div>

      <div className="space-y-4">
        {metrics.map((metric) => (
          <div key={metric.key}>
            <div className="flex justify-between text-sm mb-1">
              <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>{metric.label}</span>
              <span className={isDark ? 'text-slate-300' : 'text-gray-500'}>
                {metric.current.toLocaleString()} / {formatLimit(metric.limit)}
              </span>
            </div>
            <div className={`w-full rounded-full h-2.5 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
              <div
                className={`h-2.5 rounded-full transition-all ${getProgressColor(
                  metric.percentage
                )}`}
                style={{
                  width: `${Math.min(metric.percentage || 0, 100)}%`,
                }}
              ></div>
            </div>
            {metric.percentage !== null && (
              <div className={`text-xs mt-1 text-right ${isDark ? 'text-slate-300' : 'text-gray-500'}`}>
                {metric.percentage.toFixed(1)}%
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default UsageDisplay;
